import React, { Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import Resources from "../pages/Resources";
import Contacts from "../pages/Contacts";
import NotFound from "../pages/NotFound/NotFound";
import { useQuery } from "@apollo/client/react";
import { NAVIGATION_QUERY } from "../services/query";
import { INavigationData } from "../context/model";
import { useAppContext } from "../provider/AppContextProvider";

const SearchResults = lazy(() => import("../pages/SearchResults/SearchResults"));
const Announcements = lazy(() => import("../pages/Announcements/Announcements"));
const KeyDatesDeadlines = lazy(() => import("../pages/KeyDatesDeadlines"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const AcademyCalender = lazy(() => import("../pages/AcademyCalender"));

const TYPE_COMPONENT_MAP: Record<string, React.LazyExoticComponent<any>> = {
  Dashboard: lazy(() => import("../pages/Dashboard")),
  MarketingResources: lazy(() => import("../pages/MarketingResources")),
  Academy: lazy(() => import("../pages/Academy")),
  Agronomy: lazy(() => import("../pages/Agronomy")),
  BusinessResources: lazy(() => import("../pages/BusinessResources")),
  ResourcesNavigation: lazy(() => import("../pages/ResourcesNavigation/ResourcesNavigation")),
  MyDocuments: lazy(() => import("../pages/MyDocuments")),
};

/** Matches the sanitize logic in Header.tsx so paths stay in sync. */
const sanitizeUrl = (title: string) =>
  title
    .trim()
    .replace(/[^a-zA-Z0-9-]/g, " ")
    .replace(/\s+/g, "-")
    .toLocaleLowerCase();


const RouteList = () => {
  const { dashboardListId, isInitializing } = useAppContext();
  const validDashboardIds =
    dashboardListId?.length > 0 ? dashboardListId : [];

  // Apollo returns the cached result instantly when Header.tsx has already
  // fetched this query with the same variables — no extra network request.
  const { data: dataNavigation, loading: navLoading } = useQuery<INavigationData>(
    NAVIGATION_QUERY,
    {
      skip: isInitializing || validDashboardIds.length === 0,
      variables: { dashboardIds: validDashboardIds },
    }
  );

  // True while we have not yet determined which dynamic routes exist.
  // Suppresses the catch-all so it cannot flash before routes are ready.
  const isNavPending = isInitializing || validDashboardIds.length === 0 || navLoading;

  const navigationItems =
    dataNavigation?.cANavigationCollection.items.filter(
      (item) => item.active && !item.isExternal && !!item.types
    ) ?? [];

  return (
    <Suspense>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        {navigationItems.map((item) => {
          const Component = TYPE_COMPONENT_MAP[item.types];
          if (!Component) return null;
          const path = `/${sanitizeUrl(item.title)}`;
          return <Route key={item.types} path={path} element={<Component />} />;
        })}
        <Route path="/announcements" element={<Announcements />} />
        <Route path="/keydate-deadlines" element={<KeyDatesDeadlines />} />
        <Route path="/search-results" element={<SearchResults />} />
        <Route path="/:resourceAliasPath/:resourceAliasPath1" element={<Resources />} />
        <Route path="/academy-calendar" element={<AcademyCalender />} />
        <Route path="/event-calendar" element={<AcademyCalender />} />
        <Route path="/contacts" element={<Contacts />} />
        {!isNavPending && <Route path="*" element={<NotFound />} />} {/* Catch-all only after nav data is ready */}
      </Routes>
    </Suspense>
  );
};

export default RouteList;
